/**
 * projectPricing - reading a project's pricing off the record, and writing it
 * back.
 *
 * Two pure functions so the pane (which reads) and the editor (which writes)
 * cannot disagree about the spelling of a field. The project API is
 * snake_case throughout, unlike the matter's PascalCase edit payload, so the
 * wire shape and the record shape are the same here.
 */

import {
  PricingValue,
  DEFAULT_PRICING_MODEL,
  requiresFixedFee,
  requiresContingency,
} from '../../shared/economics/pricingModel';
import type { PricingModel } from '../../../types/economics';

/** The org's currency is not on the project record; fall back to a blank picker. */
export const projectPricingValue = (project: any): PricingValue => ({
  pricing_model: (project?.pricing_model as PricingModel) || DEFAULT_PRICING_MODEL,
  currency: (project?.currency || '').toLowerCase(),
  fixed_fee_amount: project?.fixed_fee_amount != null ? String(project.fixed_fee_amount) : '',
  budget_amount: project?.budget_amount != null ? String(project.budget_amount) : '',
  // Stored as a fraction, shown as a percentage.
  contingency_pct: project?.contingency_pct != null ? String(Number(project.contingency_pct) * 100) : '',
});

/**
 * The edit payload. Budget is sent on EVERY model, and sent as `null` when
 * cleared - omitting it would make the field write-once, with no way to remove
 * a budget set by mistake.
 */
export const projectPricingPayload = (value: PricingValue): Record<string, any> => {
  const payload: Record<string, any> = { pricing_model: value.pricing_model };

  const currency = (value.currency || '').trim().toLowerCase();
  if (currency) payload.currency = currency;

  const budget = value.budget_amount.trim();
  payload.budget_amount = budget === '' ? null : Number(budget);

  if (requiresFixedFee(value.pricing_model) && value.fixed_fee_amount.trim() !== '') {
    payload.fixed_fee_amount = Number(value.fixed_fee_amount);
  }
  if (requiresContingency(value.pricing_model) && value.contingency_pct.trim() !== '') {
    payload.contingency_pct = Number(value.contingency_pct) / 100;
  }
  return payload;
};

/** `12500` + `usd` → `USD 12,500`. Currency-less amounts still read as numbers. */
export const formatMoney = (amount: number | null | undefined, currency?: string): string | null => {
  if (amount === null || amount === undefined || (amount as unknown) === '') return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  const formatted = n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return currency ? `${currency.toUpperCase()} ${formatted}` : formatted;
};
